/**
 * 아바타용 이미지 정사각 center-crop + 리사이즈 (design-20260606 §17.1).
 *
 * 입력 이미지를 가운데 정사각으로 잘라 size×size 로 축소한 뒤 image/png Blob 으로 반환한다.
 * 결과는 항상 PNG(매직바이트 검증 가능·알파 보존) 이며 512² 기준 수백 KB → 2MB 상한 자연 충족.
 * 클라이언트에서 재인코딩하므로 서버에는 항상 image/png 가 업로드된다.
 */
export async function cropAvatarToSquarePng(file: File, size = 512): Promise<Blob> {
  const bitmap = await loadBitmap(file);
  try {
    const srcSize = Math.min(bitmap.width, bitmap.height);
    const sx = Math.floor((bitmap.width - srcSize) / 2);
    const sy = Math.floor((bitmap.height - srcSize) / 2);

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context unavailable');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, sx, sy, srcSize, srcSize, 0, 0, size, size);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('canvas.toBlob 실패');
    return blob;
  } finally {
    if ('close' in bitmap && typeof (bitmap as ImageBitmap).close === 'function') {
      (bitmap as ImageBitmap).close();
    }
  }
}

/** createImageBitmap 우선, 미지원 환경은 <img> 디코드 폴백. */
async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      /* 폴백 */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('image decode 실패'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}
